import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader2, RotateCcw, Mic, AudioLines, Send, Upload, FileCheck2, ArrowRight, Keyboard, X, Camera, Power } from "lucide-react";
import AvatarCanvas from "../components/AvatarCanvas";
import DocumentCaptureOverlay from "../components/DocumentCaptureOverlay";
import { speak, cancelSpeech, startRecognition, startRemoteRecording, startWakeWordListener, playListenEndChime } from "../lib/speech";
import { RETURN_TO_VOICE_KEY } from "../lib/kioskFlow";
import api, { formatApiErrorDetail } from "../lib/api";

// Messaggio di fallback quando l'azione non ha un outcome configurato per instradare
// (vedi onButton sotto): con azioni generiche non c'è più un vocabolario di dominio da
// descrivere — solo l'errore, se presente. Il resto del feedback viene dagli "esiti"
// configurati sull'elemento (ProjectFlowElement.outcomes, vedi action_runner.py).
function describeActionResult(result) {
  if (!result) return null;
  return result.error || null;
}

// Una lista di varianti (step.tts / element.button_tts) -> una scelta a caso; una sola voce
// resta sempre quella. Tollera anche la vecchia forma a stringa singola e liste vuote/assenti.
function pickVariant(list) {
  if (typeof list === "string") return list || null;
  if (!Array.isArray(list) || list.length === 0) return null;
  if (list.length === 1) return list[0];
  return list[Math.floor(Math.random() * list.length)];
}

// Sostituisce {{dot.path}} in un testo (step.tts/button_tts/outcome.tts) con
// answers[dot.path] — stesso dot-path già usato altrove nel progetto (outcome_field,
// scalarResponseFields/flattenScalarFields qui sotto) per i dati delle risposte delle
// azioni. Se il valore non c'è (ancora) il segnaposto resta visibile invariato, piuttosto
// che sparire silenziosamente — più facile da notare/debuggare da chi configura il flusso.
//
// Un secondo namespace opzionale, {{result.dot.path}} (resultAnswers, passato solo da
// onButton per gli outcome.tts), legge invece SOLO la risposta appena arrivata dall'ultima
// azione del click corrente — bypassa "answers" e quindi il suo "primo-arrivato-vince"
// (vedi onButton/scalarResponseFields sotto): utile quando lo stesso campo (es. "first_name"
// di un GuestOut, o "extracted.firstName.0.value" di una verifica documento) arriva più
// volte nella stessa sessione per persone/risorse diverse (es. un flusso che registra più
// ospiti in sequenza) e {{dot.path}} mostrerebbe sempre il valore della PRIMA volta invece
// di quello fresco di questo click.
function interpolate(text, answers, resultAnswers) {
  if (!text) return text;
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, path) => {
    const value = resultAnswers && path.startsWith("result.")
      ? resultAnswers[path.slice("result.".length)]
      : answers?.[path];
    return value === undefined || value === null ? match : String(value);
  });
}

// "Ciclo for"/instradamento condizionale gestito dal flusso (vedi backend/models.py::
// ProjectFlowElement.visible_if, ProjectFlowStep.skip_if): un dot-path (eventualmente con
// {{altro.dot.path}} interpolato da "answers", stesso linguaggio di interpolate() sopra)
// risolto contro "answers" PRIMA di controllare se esiste — un "!" davanti nega la
// condizione. Usato sia per nascondere un pulsante (visible_if) sia per saltare interamente
// uno step (skip_if, vedi resolveStepKey sotto) in base a un dato già disponibile, invece di
// scoprirlo solo dopo un click o mostrare comunque lo step (es. "c'è un altro ospite?" via
// stays.0.guest_ids.{{loop_index}}, già nella risposta di get_reservation).
//
// Forma "a.dot.path=b.dot.path" (confronto tra DUE dot-path, non solo "esiste"): entrambi i
// lati interpolati e letti da "answers", poi confrontati per VALORE — usato per riconoscere
// quando un giro del ciclo ricade sullo stesso elemento già gestito altrove (es. l'ospite
// che corrisponde a reference_guest_id, già registrato come intestatario, ricompare in
// mezzo al ciclo "altri ospiti" perché l'array guest_ids non è ordinato per ruolo).
function conditionMet(condition, answers) {
  const negate = condition.startsWith("!");
  const expr = negate ? condition.slice(1) : condition;
  const eqIdx = expr.indexOf("=");
  if (eqIdx !== -1) {
    const leftPath = interpolate(expr.slice(0, eqIdx), answers);
    const rightPath = interpolate(expr.slice(eqIdx + 1), answers);
    const leftVal = answers?.[leftPath];
    const equal = leftVal !== undefined && leftVal === answers?.[rightPath];
    return negate ? !equal : equal;
  }
  const path = interpolate(expr, answers);
  const exists = answers?.[path] !== undefined && answers?.[path] !== null;
  return negate ? !exists : exists;
}

function isElementVisible(el, answers) {
  return !el.visible_if || conditionMet(el.visible_if, answers);
}

// ProjectFlowStep.skip_if/skip_to: se la condizione (stesso linguaggio di isElementVisible
// sopra) è vera, lo step non viene mai mostrato — si salta direttamente a skip_to (es. lo
// step "vuoi registrare altri ospiti?" del check-in HosPass non ha senso chiederlo quando la
// prenotazione ha un solo ospite: skip_if="!stays.0.guest_ids.1", skip_to="done_checkin").
// Segue la catena (un salto può portare a un altro step anch'esso da saltare) con un limite
// di sicurezza contro un loop di skip_to configurato male.
function resolveStepKey(key, stepsByKey, answers) {
  let hops = 0;
  while (hops++ < 10) {
    const step = stepsByKey[key];
    if (!step?.skip_if || !step.skip_to || !conditionMet(step.skip_if, answers)) return key;
    key = step.skip_to;
  }
  return key;
}

// Confronto per sottostringa case-insensitive tra l'utterance (dopo la wake word, dal
// microfono o digitata) e le frasi configurate su ciascun ProjectVoiceTrigger — il primo
// trigger con una frase trovata nel testo vince (vedi backend/models.py::ProjectVoiceTrigger
// per il razionale: apre il flusso deterministico direttamente sullo step giusto,
// scavalcando l'agente/chat). Torna la step_key del trigger, o null se nessuno combacia.
function matchVoiceTrigger(text, voiceTriggers) {
  const normalized = (text || "").trim().toLowerCase();
  if (!normalized) return null;
  for (const trigger of voiceTriggers || []) {
    for (const phrase of trigger.phrases || []) {
      const p = (phrase || "").trim().toLowerCase();
      if (p && normalized.includes(p)) return trigger.step_key;
    }
  }
  return null;
}

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.substring(0, 2), 16) || 0;
  const g = parseInt(full.substring(2, 4), 16) || 0;
  const b = parseInt(full.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Solo it/en per ora (vedi backend/schemas.py::SUPPORTED_LANGUAGES) — mappa verso i codici
// BCP47 usati da TTS/STT (Web Speech e provider cloud).
const LANG_BCP47 = { it: "it-IT", en: "en-US" };

// Testo nella lingua corrente da un dizionario lingua -> testo (welcome_message,
// suggested_prompts[].translations) — ricade sull'italiano, poi sulla prima traduzione
// disponibile, se quella scelta dal visitatore non è stata compilata.
function localizedText(translations, uiLang) {
  const t = translations || {};
  return t[uiLang] || t.it || Object.values(t)[0] || "";
}

// Campi scalari (stringa/numero/booleano) dalla risposta JSON di un'azione, appiattiti
// per dot-path (es. {"stays": [{"id": "s1"}]} -> {"stays.0.id": "s1"}) — usati per "far
// fluire" un valore tra due azioni dello stesso progetto (es. balance_cents di una
// lookup riusato come amount_cents del pagamento successivo, o stays.0.id come
// percorso dell'azione di check-in) salvandolo tra le risposte del visitatore
// (answers), lo stesso canale con cui i campi del form alimentano le azioni (vedi
// onButton sotto) — stesso dot-path già usato altrove in questo progetto per
// Action.outcome_field. Nessun limite di profondità: risposte molto annidate
// finiscono comunque tutte (appiattite) in answers.
function flattenScalarFields(value, prefix, out) {
  if (value === null || ["string", "number", "boolean"].includes(typeof value)) {
    if (prefix) out[prefix] = value;
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((v, i) => flattenScalarFields(v, prefix ? `${prefix}.${i}` : String(i), out));
    return;
  }
  if (typeof value === "object") {
    Object.entries(value).forEach(([k, v]) => flattenScalarFields(v, prefix ? `${prefix}.${k}` : k, out));
  }
}

function scalarResponseFields(response) {
  if (!response || typeof response !== "object") return {};
  const out = {};
  flattenScalarFields(response, "", out);
  return out;
}

export default function ProjectKiosk() {
  const { projectId } = useParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [currentKey, setCurrentKey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [speaking, setSpeaking] = useState(false);
  const [reply, setReply] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState("voice");
  const [chatInput, setChatInput] = useState("");
  const [listening, setListening] = useState(false);
  // true dopo che la wake word PRINCIPALE è stata detta almeno una volta in questa sessione —
  // da quel momento il kiosk ascolta ANCHE quella secondaria (più breve), più comoda da
  // ripetere per le domande successive senza dover ridire la frase intera ogni volta
  // (richiesto esplicitamente dal cliente). Azzerato da restart(): un nuovo ospite deve
  // sempre ripartire dicendo quella principale.
  const [secondaryWakeEnabled, setSecondaryWakeEnabled] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [uploading, setUploading] = useState({});
  const [captureEl, setCaptureEl] = useState(null); // elemento "document" per cui è aperta la cam
  const [stepText, setStepText] = useState(null);
  const [uiLang, setUiLang] = useState("it");
  const recRef = useRef(null);
  const wakeRef = useRef(null);
  const chatEndRef = useRef(null);
  // Contatore di "generazione" della sessione corrente — incrementato da restart() per
  // invalidare qualunque onButton() ancora in corso da PRIMA del reset (richiesto
  // esplicitamente dal cliente: "Annulla" deve davvero cancellare l'operazione, non solo
  // mostrare la home voce per un istante). Senza questo: se "Annulla" viene premuto nella
  // breve finestra tra la risposta HTTP di un'azione (sending torna false, il pulsante si
  // riabilita) e la fine della funzione onButton di quel click precedente (che deve ancora
  // elaborare l'outcome ed eventualmente chiamare goTo()), quella chiamata goTo() tardiva
  // sovrascriveva currentKey APPENA DOPO restart() lo aveva azzerato — "Annulla" sembrava
  // funzionare (si vedeva la home per un istante) ma un click successivo su "Start" riprendeva
  // dal passo in cui ci si era fermati, perché currentKey non era rimasto sul vero step
  // iniziale. Vedi i controlli "sessionGenRef.current !== myGen" sotto in onButton().
  const sessionGenRef = useRef(0);
  // Guardia sincrona anti-doppio-tap: su un totem touchscreen un singolo tocco può generare
  // più eventi "click" in rapidissima sequenza (ghost click/doppia registrazione del touch) —
  // "disabled={sending}" sul pulsante non basta perché React applica il nuovo valore solo al
  // render successivo, un istante dopo che setSending(true) viene chiamato dentro onButton:
  // nella finestra in mezzo, un secondo/terzo evento "click" arriva ancora su un pulsante non
  // disabilitato e fa partire un'altra onButton() in parallelo per lo STESSO click fisico —
  // segnalato dal cliente: dopo l'invio di un documento, la stessa frase dell'esito veniva letta
  // 3 volte (3 chiamate reali concorrenti alla stessa azione, ciascuna con il proprio goTo/say
  // in coda). sendingRef (non state: deve essere letto/scritto in modo sincrono, non al prossimo
  // render) blocca ogni nuova onButton() mentre una è già in corso, qualunque sia il pulsante.
  const sendingRef = useRef(false);

  useEffect(() => {
    if (phase === "voice" && history.length) {
      chatEndRef.current?.scrollIntoView({ block: "end" });
    }
  }, [history, phase]);

  // Avviso sonoro anche alla CHIUSURA del microfono (richiesto esplicitamente dal cliente,
  // simmetrico a quello di risveglio) — un solo punto per tutte le cause che possono portare
  // "listening" da true a false (stop manuale dal pulsante mic, silenzio/timeout del
  // riconoscimento del browser, domanda riconosciuta) invece di duplicare la chiamata in ogni
  // call site. wasListeningRef (non state) serve solo a ricordare il valore PRECEDENTE per
  // rilevare la transizione vera e propria, senza suonare anche al primo render (già false).
  const wasListeningRef = useRef(false);
  useEffect(() => {
    if (wasListeningRef.current && !listening) playListenEndChime();
    wasListeningRef.current = listening;
  }, [listening]);

  // Refresh automatico dopo N secondi di inattività (richiesto esplicitamente dal cliente,
  // configurabile per progetto dal pannello Avatar — ProjectManager.js, default 60s, 0 =
  // disabilitato): torna alla schermata iniziale come il pulsante refresh/Annulla (stesso
  // restart() qui sotto). "Inattività" non è solo l'assenza di tap/click/tasti dell'ospite:
  // finché il sistema sta facendo qualcosa per conto suo (l'avatar sta parlando, il microfono
  // sta ascoltando, un messaggio è in invio) il timer resta "vivo" ad ogni tick — altrimenti
  // rischieremmo di interrompere una risposta a metà solo perché l'ospite, nel frattempo, non
  // ha toccato nulla.
  const lastActivityRef = useRef(Date.now());
  const markActivity = useCallback(() => { lastActivityRef.current = Date.now(); }, []);

  useEffect(() => {
    const opts = { passive: true };
    window.addEventListener("pointerdown", markActivity, opts);
    window.addEventListener("keydown", markActivity, opts);
    return () => {
      window.removeEventListener("pointerdown", markActivity, opts);
      window.removeEventListener("keydown", markActivity, opts);
    };
  }, [markActivity]);
  // Il check vero e proprio (quello che chiama restart()) è più sotto, subito dopo la
  // definizione di restart() stessa — qui sopra c'è solo l'ascolto degli eventi di attività.

  const newSession = () =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const stepsByKey = useMemo(() => {
    const m = {};
    (config?.steps || []).forEach((s) => (m[s.key] = s));
    return m;
  }, [config]);

  const currentStep = currentKey ? stepsByKey[currentKey] : null;
  const lang = LANG_BCP47[uiLang] || config?.avatar?.tts_lang || "it-IT";
  const voice = config?.avatar?.tts_voice;
  const suggestedPrompts = config?.suggested_prompts || [];
  // Solo i suggerimenti con un testo effettivo nella lingua corrente (dopo il fallback di
  // localizedText) — determina sia se mostrare il pannello sia se centrare l'unico pulsante
  // o disporli in griglia (vedi il layout nel layer voce sotto).
  const visibleSuggestions = suggestedPrompts
    .map((p) => ({ id: p.id, text: localizedText(p.translations, uiLang) }))
    .filter((p) => p.text);
  const backgroundUrl = config?.avatar?.background_url;
  const modelScale = config?.avatar?.model_scale;
  const modelOffsetY = config?.avatar?.model_offset_y;
  const formTextColor = config?.avatar?.form_text_color || "#FFFFFF";
  const formInputColor = config?.avatar?.form_input_color || "#1E1E1E";
  const formButtonColor = config?.avatar?.form_button_color || "#0055FF";
  // Ambra fissa (non legata al tema/colore configurabile del progetto, a differenza di
  // formButtonColor): serve solo a distinguere visivamente l'anello del microfono mentre
  // l'avatar PARLA da quello mentre il microfono ASCOLTA davvero (stessa icona AudioLines per
  // entrambi, richiesto dal cliente) — scelta esplicita del cliente ("Ambra, vai pure con
  // quella").
  const micSpeakingRingColor = "#F59E0B";
  const formOpacity = config?.avatar?.form_opacity ?? 0.55;
  const formPanelBg = hexToRgba("#0A0A0A", formOpacity);
  const formBlur = config?.avatar?.form_blur ?? 12;
  const formBlurStyle = { backdropFilter: `blur(${formBlur}px)`, WebkitBackdropFilter: `blur(${formBlur}px)` };
  const ttsCloudEnabled = !!config?.tts_cloud_enabled;
  const sttCloudEnabled = !!config?.stt_cloud_enabled;

  useEffect(() => {
    api
      .get(`/api/projects/public/${projectId}`)
      .then(({ data }) => {
        setConfig(data);
        const start = (data.steps || []).find((s) => s.is_start) || data.steps?.[0];
        setCurrentKey(start?.key || null);
        setSessionId(newSession());
        // Lingua iniziale del selettore dedotta dalla lingua TTS configurata per il progetto —
        // il visitatore può comunque cambiarla dal selettore in schermata iniziale.
        const base = (data.avatar?.tts_lang || "it").slice(0, 2).toLowerCase();
        setUiLang(base === "en" ? "en" : "it");
      })
      .catch((e) => setError(e.response?.data?.detail || "Agente non disponibile"));
  }, [projectId]);

  const say = useCallback(
    (texts, onEnd) => {
      speak(texts, {
        lang, voiceName: voice,
        useRemote: ttsCloudEnabled, projectId,
        onStart: () => setSpeaking(true),
        onEnd: () => { setSpeaking(false); onEnd && onEnd(); },
      });
    },
    [lang, voice, ttsCloudEnabled, projectId]
  );

  const justEnteredFormRef = useRef(false);

  useEffect(() => {
    if (phase === "form" && currentStep) {
      setValidationError(null);
      const text = interpolate(pickVariant(currentStep.tts), answers);
      setStepText(text);
      const withWelcome = justEnteredFormRef.current;
      justEnteredFormRef.current = false;
      const welcome = localizedText(config?.avatar?.welcome_message, uiLang);
      say(withWelcome ? [welcome, text] : text);
    }
  }, [currentKey, phase]); // eslint-disable-line

  useEffect(() => {
    if (phase !== "form" || !currentStep?.auto_return_seconds) return;
    const timer = setTimeout(() => restart(), currentStep.auto_return_seconds * 1000);
    return () => clearTimeout(timer);
  }, [currentKey, phase]); // eslint-disable-line

  // Rete di sicurezza: si dovrebbe entrare in "form" solo tramite enterForm(), che oggi è
  // nascosto quando il progetto non ha alcuno step (vedi il pulsante "Start" sotto) — ma se
  // per qualunque motivo ci si ritrova in questa fase senza uno step valido (flusso svuotato
  // mentre una sessione era già a metà, stato non aggiornato, ecc.), si torna alla voce invece
  // di restare bloccati sullo spinner o rompere il render sugli elementi dello step.
  useEffect(() => {
    if (config && phase === "form" && !currentStep) {
      setPhase("voice");
    }
  }, [config, phase, currentStep]);

  const enterForm = () => {
    stopWakeListener();
    cancelSpeech();
    justEnteredFormRef.current = true;
    setPhase("form");
  };

  // Apre il layer 2 (form, deterministico) direttamente su uno step preciso invece che
  // sul primo (is_start) — usato dai trigger vocali sotto. Torna false (non fa nulla) se
  // lo step non esiste più nel flusso corrente (es. rinominato/cancellato dal pannello).
  const enterFormAtStep = (stepKey) => {
    if (!stepsByKey[stepKey]) return false;
    stopWakeListener();
    cancelSpeech();
    justEnteredFormRef.current = true;
    setCurrentKey(resolveStepKey(stepKey, stepsByKey, answers));
    setPhase("form");
    return true;
  };

  const setAnswer = (k, v) => {
    setValidationError(null);
    setAnswers((a) => ({ ...a, [k]: v }));
  };

  const validate = (step) => {
    for (const el of step.elements) {
      if (!isElementVisible(el, answers)) continue;
      if (el.kind === "text" && el.required && !(answers[el.field_key] || "").trim()) {
        return `Compila: ${el.label}`;
      }
      if (el.kind === "checkbox" && el.required && !answers[el.field_key]) {
        return `Obbligatorio: ${el.label}`;
      }
      if (el.kind === "file" && el.required && !answers[el.field_key]) {
        return `Carica il documento: ${el.label}`;
      }
      if (el.kind === "document" && el.required && !answers[el.field_key]) {
        return `Scansiona il documento: ${el.label}`;
      }
    }
    return null;
  };

  const uploadFile = async (el, fileObj) => {
    if (!fileObj) return;
    setValidationError(null);
    const fd = new FormData();
    fd.append("file", fileObj, fileObj.name || "document.jpg");
    if (sessionId) fd.append("session_id", sessionId);
    if (el.field_key) fd.append("field_key", el.field_key);
    setUploading((u) => ({ ...u, [el.field_key]: true }));
    try {
      const { data } = await api.post(`/api/projects/public/${projectId}/upload`, fd);
      setAnswer(el.field_key, data.id);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setValidationError(msg);
      say(msg);
    } finally {
      setUploading((u) => ({ ...u, [el.field_key]: false }));
    }
  };

  const goTo = (key, answersOverride) => {
    if (key === RETURN_TO_VOICE_KEY) {
      restart();
      return;
    }
    // answersOverride: onButton passa "latestAnswers" (i dati aggiornati DI QUESTO click,
    // set_fields/azione/increment_field inclusi) invece di lasciar leggere lo stato React
    // "answers" qui sotto — quello si aggiorna solo al prossimo render, quindi uno skip_if
    // che dipende da un dato appena scritto nello STESSO click (es. checkin_document che
    // controlla stays.0.state subito dopo che lo stesso pulsante ha chiamato get_reservation)
    // lo vedrebbe sempre assente e non scatterebbe mai. Nessun altro chiamante passa questo
    // parametro: per loro answers (closure) è già corretto, essendo un click successivo.
    const effectiveAnswers = answersOverride || answers;
    if (key && stepsByKey[key]) setCurrentKey(resolveStepKey(key, stepsByKey, effectiveAnswers));
  };

  const onButton = async (el) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      await onButtonImpl(el);
    } finally {
      sendingRef.current = false;
    }
  };

  const onButtonImpl = async (el) => {
    const myGen = sessionGenRef.current;
    const stale = () => sessionGenRef.current !== myGen;
    const err = validate(currentStep);
    if (err) {
      setValidationError(err);
      say(err);
      return;
    }
    setValidationError(null);
    setReply(null);
    const utterances = [];
    const buttonTts = interpolate(pickVariant(el.button_tts), answers);
    if (buttonTts) utterances.push(buttonTts);

    // "Ciclo for" gestito dal flusso (vedi isElementVisible sopra e backend/models.py::
    // ProjectFlowElement): set_fields scrive valori letterali (eventualmente con
    // {{dot.path}}) in "answers" PRIMA di eseguire l'azione sullo stesso click — usato per
    // inizializzare un indice di loop all'ingresso (es. "loop_index": "1"). latestAnswers
    // (non "answers", che si aggiorna solo al prossimo render) è quello che la catena di
    // azioni sotto userà davvero per questo click.
    let latestAnswers = answers;
    if (el.set_fields) {
      const updates = {};
      for (const [k, v] of Object.entries(el.set_fields)) updates[k] = interpolate(String(v), latestAnswers);
      latestAnswers = { ...latestAnswers, ...updates };
      setAnswers((a) => ({ ...a, ...updates }));
    }

    let actionFailed = false;
    let outcomeConf = null;
    let nextStepKey = el.next_step_key;
    if (el.action) {
      // el.action: una o più Action.key separate da virgola, eseguite IN SEQUENZA sullo
      // stesso pulsante (es. cerca prenotazione -> recupera l'ospite, per poter leggere
      // nome/cognome nel messaggio dell'esito) — la risposta di ciascuna arricchisce
      // "answers" (stesso canale già usato per far fluire dati tra azioni, vedi
      // scalarResponseFields sotto) prima di eseguire la successiva. Se una fallisce
      // (outcome diverso da "success"/"field_*", cioè non un 2xx) la catena si ferma lì:
      // l'esito/i dati mostrati sono quelli dell'ULTIMA azione eseguita, successo o errore.
      const actionKeys = el.action.split(",").map((k) => k.trim()).filter(Boolean);
      let result = null;
      setSending(true);
      try {
        for (const actionKey of actionKeys) {
          const { data } = await api.post(`/api/projects/public/${projectId}/actions/${actionKey}`, {
            fields: latestAnswers,
          });
          // "Annulla" (restart()) può scattare mentre questa richiesta era in volo — se è
          // successo, questo click non esiste più: abbandona senza toccare answers/currentKey
          // (vedi la nota su sessionGenRef sopra).
          if (stale()) return;
          result = data;
          // Primo-arrivato-vince: non sovrascrivere una chiave già presente in "answers".
          // Risposte di azioni diverse condividono spesso nomi di campo comuni (quasi ogni
          // risorsa REST ha un "id" proprio) — senza questa guardia, l'id della prenotazione
          // (scritto da get_reservation) veniva sovrascritto dall'id del record appena creato
          // da un'azione successiva (es. l'id del movimento di conto creato da un "aggiungi
          // extra"), rompendo il reservation_id delle chiamate successive nello stesso step.
          const carried = scalarResponseFields(data?.response);
          const newCarried = Object.fromEntries(Object.entries(carried).filter(([k]) => !(k in latestAnswers)));
          if (Object.keys(newCarried).length) {
            latestAnswers = { ...latestAnswers, ...newCarried };
            setAnswers((a) => ({ ...a, ...newCarried }));
          }
          const succeeded = data?.outcome === "success" || (data?.outcome || "").startsWith("field_");
          if (!succeeded) break;
        }
        if (result?.outcome && el.outcomes && el.outcomes[result.outcome]) {
          outcomeConf = el.outcomes[result.outcome];
          // {{result.dot.path}} nel testo dell'esito legge la risposta GREZZA dell'ultima
          // azione del click corrente (vedi interpolate sopra) — mai "incastrata" come
          // answers da primo-arrivato-vince.
          const resultAnswers = scalarResponseFields(result?.response);
          const msg = interpolate(pickVariant(outcomeConf.tts), latestAnswers, resultAnswers);
          if (msg) {
            setReply(msg);
            utterances.push(msg);
          }
          nextStepKey = outcomeConf.next_step_key || null;
        } else {
          const desc = describeActionResult(result);
          if (result?.error) actionFailed = true;
          if (desc) {
            setReply(desc);
            utterances.push(desc);
          }
        }
      } catch (e) {
        actionFailed = true;
        const msg = formatApiErrorDetail(e.response?.data?.detail) || "Azione non riuscita, riprova.";
        setValidationError(msg);
        utterances.push(msg);
      } finally {
        setSending(false);
      }
    }

    // increment_field: incrementa di 1 un indice di loop DOPO l'azione (non prima: per
    // submit_document_next_guest sotto, l'azione deve ancora leggere l'indice CORRENTE —
    // vedi backend/models.py::ProjectFlowElement) — prepara answers per il prossimo giro
    // (es. da "Invia documento" dell'ospite N, così "Ospite successivo"/checkin_guest_confirm
    // sa già se esiste l'ospite N+1 senza bisogno di una chiamata di prova).
    // !actionFailed: con una chiamata VERAMENTE fallita (errore di rete/5xx, result.error —
    // non il caso "verificato? no" senza errore, che instrada su checkin_guest_document_retry
    // apposta per quello) lo step NON cambia (si esce prima, sotto, senza chiamare goTo): resta
    // lo stesso checkin_guest_document per far ricliccare "Invia documento" sullo stesso
    // ospite. Incrementare comunque qui (comportamento precedente) faceva avanzare loop_index
    // SENZA che l'utente se ne accorgesse: il reclick successivo rimandava il documento sotto
    // il guest_id SBAGLIATO (quello dell'ospite dopo), lasciando l'ospite vero senza documento
    // ma già "check-in" nel gestionale (check_in_guest_next è comunque andato a buon fine prima
    // del fallimento di submit_document_next_guest) — segnalato dal cliente: "manca un ospite
    // per fare la scansione dei documenti, ma li trovo tutti e tre registrati nel pms".
    if (el.increment_field && !actionFailed) {
      const current = Number(latestAnswers[el.increment_field]);
      const next = (Number.isFinite(current) ? current : 0) + 1;
      latestAnswers = { ...latestAnswers, [el.increment_field]: next };
      setAnswers((a) => ({ ...a, [el.increment_field]: next }));
    }

    if (!actionFailed && el.button_message) {
      setSending(true);
      try {
        const { data } = await api.post("/api/projects/public/message", {
          project_id: projectId, message: el.button_message, session_id: sessionId, lang: uiLang,
        });
        if (stale()) return;
        if (data?.reply) {
          setReply(data.reply);
          utterances.push(data.reply);
        }
      } catch {
        /* ignore */
      } finally {
        setSending(false);
      }
    }

    if (stale()) return;
    if (actionFailed) {
      if (utterances.length) say(utterances);
      return;
    }
    if (utterances.length) say(utterances, () => { if (!stale()) goTo(nextStepKey, latestAnswers); });
    else goTo(nextStepKey, latestAnswers);
  };

  const restart = () => {
    // Invalidare SUBITO qualunque onButton() di un click precedente ancora in corso (vedi la
    // nota su sessionGenRef sopra) — prima di qualunque altro reset, così anche un controllo
    // "stale()" valutato nell'istante stesso in cui restart() comincia a girare lo vede già
    // aggiornato.
    sessionGenRef.current += 1;
    cancelSpeech();
    setAnswers({});
    setReply(null);
    setValidationError(null);
    setHistory([]);
    setSessionId(newSession());
    setSecondaryWakeEnabled(false);
    setPhase("voice");
    const start = (config?.steps || []).find((s) => s.is_start) || config?.steps?.[0];
    setCurrentKey(start?.key || null);
  };

  // Check vero e proprio del refresh automatico (vedi lastActivityRef/markActivity sopra, dove
  // vengono registrati gli eventi di attività): deve stare DOPO restart() perché lo richiama
  // direttamente.
  useEffect(() => {
    const timeoutSeconds = config?.avatar?.idle_timeout_seconds;
    if (!timeoutSeconds) return undefined; // 0/non configurato ancora = disabilitato
    const timeoutMs = timeoutSeconds * 1000;
    const id = setInterval(() => {
      if (speaking || listening || sending) {
        markActivity();
        return;
      }
      if (Date.now() - lastActivityRef.current >= timeoutMs) {
        markActivity();
        restart();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [config?.avatar?.idle_timeout_seconds, speaking, listening, sending, restart, markActivity]);

  const sendChat = async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending) return;
    stopWakeListener();
    setChatInput("");

    // Trigger vocale: apre direttamente lo step configurato invece di passare dalla chat
    // dell'agente — deterministico, nessuna chiamata LLM. Controllato PRIMA di toccare la
    // cronologia della chat (che appartiene solo al layer voce, non più rilevante appena
    // si entra nel form).
    const triggeredStep = matchVoiceTrigger(msg, config?.voice_triggers);
    if (triggeredStep && enterFormAtStep(triggeredStep)) return;

    const h = [...history, { role: "user", content: msg }];
    setHistory(h);
    setSending(true);
    try {
      const { data } = await api.post("/api/projects/public/message", {
        project_id: projectId, message: msg, history: h.slice(-10), session_id: sessionId, lang: uiLang,
      });
      if (data?.reply) {
        setReply(data.reply);
        setHistory([...h, { role: "assistant", content: data.reply }]);
        say(data.reply);
      }
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const toggleMic = () => {
    if (listening) {
      recRef.current?.stop?.();
      setListening(false);
      return;
    }
    // Ferma sempre l'ascolto passivo della wake word prima di avviarne uno attivo: due sessioni
    // SpeechRecognition contemporanee non sono valide (spec Web Speech API) e possono lasciare il
    // microfono/motore vocale del browser in uno stato inconsistente, con effetti a cascata anche
    // sulla sintesi vocale successiva.
    stopWakeListener();
    cancelSpeech();
    const onResult = (t) => { setListening(false); sendChat(t); };
    const onEnd = () => setListening(false);
    // Provider cloud configurato per il progetto: push-to-talk (registra finché non si
    // riclicca). Browser: riconoscimento continuo, si ferma da solo al silenzio.
    const rec = sttCloudEnabled
      ? startRemoteRecording(projectId, lang, { onResult, onEnd })
      : startRecognition(lang, { onResult, onEnd });
    recRef.current = rec;
    if (rec) setListening(true);
  };

  const stopWakeListener = useCallback(() => {
    wakeRef.current?.stop?.();
    wakeRef.current = null;
  }, []);

  // Ascolto passivo della wake word (es. "Ciao Gaia", config.avatar.wake_word) — solo nel
  // layer voce, e mai mentre l'avatar parla/ascolta già attivamente/sta elaborando una
  // richiesta (altrimenti l'ascolto continuo del browser si accavallerebbe col resto).
  // Detta la wake word, se la frase prosegue oltre ("Ciao Gaia, che orari avete?") la parte
  // restante è inviata subito come domanda; se la wake word è tutto ciò che si è detto, il
  // kiosk rilegge il messaggio di benvenuto e poi apre il microfono in attesa della domanda
  // (stesso comportamento del pulsante microfono).
  // activeWakeWords: la principale sempre, la secondaria SOLO una volta che quella principale
  // è già stata detta in questa sessione (secondaryWakeEnabled) — richiesto dal cliente, non
  // un'alternativa equivalente fin dall'inizio.
  const primaryWakeWord = config?.avatar?.wake_word;
  const secondaryWakeWord = config?.avatar?.wake_word_secondary;
  const activeWakeWords = useMemo(
    () => [primaryWakeWord, secondaryWakeEnabled ? secondaryWakeWord : null].filter(Boolean),
    [primaryWakeWord, secondaryWakeWord, secondaryWakeEnabled]
  );
  useEffect(() => {
    if (phase !== "voice" || speaking || listening || sending || activeWakeWords.length === 0) {
      stopWakeListener();
      return;
    }
    wakeRef.current = startWakeWordListener(lang, activeWakeWords, {
      onWake: (rest, matchedWord) => {
        stopWakeListener();
        cancelSpeech();
        if (secondaryWakeWord && matchedWord === primaryWakeWord) setSecondaryWakeEnabled(true);
        const text = (rest || "").trim();
        if (text) {
          sendChat(text);
        } else if (matchedWord === primaryWakeWord) {
          const welcome = localizedText(config?.avatar?.welcome_message, uiLang);
          say(welcome, () => toggleMic());
        } else {
          // Wake word SECONDARIA detta da sola (nessuna domanda subito dopo): niente messaggio
          // di benvenuto — l'ospite l'ha già sentito dicendo quella principale la prima volta,
          // ripeterlo ogni volta che si usa la scorciatoia sarebbe fastidioso (segnalato dal
          // cliente). Apre il microfono direttamente.
          toggleMic();
        }
      },
    });
    return stopWakeListener;
  }, [phase, speaking, listening, sending, lang, activeWakeWords]); // eslint-disable-line

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-kiosk-bg text-white">
        <p data-testid="kiosk-error">{error}</p>
      </div>
    );
  }
  if (!config || (phase === "form" && !currentStep)) {
    // Il layer 1 "voce" (sotto) non dipende da currentStep — funziona anche a progetto senza
    // alcun flusso configurato. Solo il layer 2 "form" ne ha bisogno per forza: se si arriva qui
    // in phase="form" senza step (caso limite, impedito nel resto normale nascondendo "Start"
    // quando non c'è flusso — vedi sotto), l'effect qui sopra riporta alla voce; nel frattempo
    // resta lo spinner invece di rompere il render su currentStep.elements.
    return (
      <div className="flex h-screen items-center justify-center bg-kiosk-bg text-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // ---------- Layer 1: avatar + microfono ----------
  if (phase === "voice") {
    return (
      <div className="relative flex h-screen flex-col items-center bg-kiosk-bg text-white" data-testid="kiosk-root" data-kiosk-phase="voice">
        <AvatarCanvas
          provider={config.avatar.provider} modelUrl={config.avatar.model_url} backgroundUrl={backgroundUrl}
          speaking={speaking} modelScale={modelScale} modelOffsetY={modelOffsetY} still={!speaking}
          idleClip={config.avatar.idle_animation_clip} speakingBodyClip={config.avatar.speaking_body_animation_clip}
          speakingLipsClip={config.avatar.speaking_lips_animation_clip}
          className="absolute inset-0 h-full w-full"
        />

        <div className="absolute left-6 top-6 z-10 flex items-center gap-1 sm:left-10 sm:top-10" data-testid="kiosk-lang-selector">
          {/* Richiesto dal cliente: tasto on/off a sinistra dei tasti lingua, per chiudere la
              scheda del browser — window.close() funziona solo se il browser lo permette
              (tipicamente una scheda aperta da script, o un browser kiosk configurato per
              consentirlo): nessun fallback qui, è una scelta del setup del dispositivo, non
              qualcosa che possiamo garantire lato frontend. */}
          <button
            onClick={() => window.close()}
            data-testid="kiosk-close-btn"
            title="Chiudi"
            className="flex h-7 w-7 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white"
            style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
          >
            <Power className="h-4 w-4" />
          </button>
          {Object.keys(LANG_BCP47).map((code) => (
            <button
              key={code}
              onClick={() => setUiLang(code)}
              data-testid={`kiosk-lang-${code}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${uiLang === code ? "text-white" : "text-white/60 hover:text-white/90"}`}
              style={{ backgroundColor: uiLang === code ? formButtonColor : "rgba(255,255,255,0.1)" }}
            >
              {code}
            </button>
          ))}
        </div>

        <div className="relative z-10 mt-10 flex flex-col items-center gap-2 px-6 text-center">
          <span className="font-display text-sm uppercase tracking-[0.3em] text-white/60">{config.project_name}</span>
          {!config.avatar.hide_welcome_text && (
            <h1 className="mt-4 max-w-lg font-display text-3xl font-black leading-tight" data-testid="kiosk-welcome-title">
              {localizedText(config.avatar.welcome_message, uiLang)}
            </h1>
          )}
        </div>

        {history.length === 0 && visibleSuggestions.length > 0 && (
          <div
            className="absolute inset-x-0 bottom-0 z-10 h-[50vh] overflow-y-auto rounded-t-3xl px-6 pb-6 pt-8 sm:px-10"
            style={{ backgroundColor: formPanelBg, borderTop: `1px solid ${hexToRgba(formTextColor, 0.14)}`, ...formBlurStyle }}
            data-testid="kiosk-suggested-prompts"
          >
            {/* Tornati alla griglia 2 colonne (misunderstanding sul giro precedente: erano
                già corretti così, il cliente intendeva i pulsanti IN ALTO — mic/refresh —
                vedi sotto, non questi). */}
            <div
              className={`mx-auto w-full max-w-2xl gap-2.5 ${
                visibleSuggestions.length === 1 ? "flex justify-center" : "grid grid-cols-1 sm:grid-cols-2"
              }`}
            >
              {visibleSuggestions.map((p) => (
                <button
                  key={p.id} onClick={() => sendChat(p.text)} disabled={sending}
                  data-testid={`kiosk-suggestion-${p.id}`}
                  // Stessa dimensione dei pulsanti del flusso principale, poi ingranditi
                  // entrambi del 20% su richiesta esplicita del cliente (px-6 py-[1.05rem]
                  // text-[1.2rem] = px-5/py-3.5/text-base * 1.2; prima ancora: px-4 py-2.5
                  // text-sm font-medium).
                  className={`border px-6 py-[1.05rem] text-[1.2rem] font-semibold text-white/90 transition-opacity hover:opacity-80 disabled:opacity-50 ${
                    visibleSuggestions.length === 1 ? "w-full max-w-md" : ""
                  }`}
                  style={{ backgroundColor: hexToRgba(formButtonColor, 0.18), borderColor: hexToRgba(formButtonColor, 0.4) }}
                >
                  {p.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div
            className="absolute inset-x-0 bottom-0 z-10 flex h-[50vh] flex-col overflow-y-auto rounded-t-3xl px-6 pb-6 pt-8 sm:px-10"
            style={{ backgroundColor: formPanelBg, borderTop: `1px solid ${hexToRgba(formTextColor, 0.14)}`, ...formBlurStyle }}
            data-testid="kiosk-chat-transcript"
          >
            {/* Niente più "mt-auto": richiesto esplicitamente dal cliente ("messaggi dall'alto
                verso il basso invece che dal basso verso l'alto") — prima, con poche righe di
                conversazione, i messaggi restavano incollati in fondo al pannello (mt-auto li
                spingeva giù, lasciando vuoto sopra); ora partono subito sotto il padding
                superiore del pannello e crescono verso il basso come la history si allunga.
                chatEndRef sotto continua comunque a tenere visibile l'ultimo messaggio quando
                la conversazione supera l'altezza del pannello. */}
            <div className="mx-auto flex w-full max-w-xl flex-col gap-3">
              {history.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {/* Fumetti ingranditi (richiesto esplicitamente dal cliente, erano troppo
                      piccoli): px-4 py-2.5 text-sm -> px-5 py-3.5 text-base, stesso padding/
                      dimensione testo dei pulsanti del flusso principale. */}
                  <div
                    className="max-w-[80%] rounded-2xl px-5 py-3.5 text-base leading-snug"
                    style={m.role === "user" ? { backgroundColor: formButtonColor, color: "#fff" } : { backgroundColor: hexToRgba(formTextColor, 0.12), color: formTextColor }}
                    data-testid={`kiosk-chat-bubble-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          </div>
        )}

        <div className="absolute right-6 top-6 z-10 flex flex-col items-end gap-3 sm:right-10 sm:top-10">
          {/* mic/refresh in colonna (non più affiancati orizzontalmente): misunderstanding
              chiarito dal cliente dopo il giro precedente — erano questi due pulsanti, non i
              suggerimenti, a dover cambiare allineamento. items-start sul contenitore esterno
              così il fumetto della wake word resta accostato al primo pulsante (il mic, quello
              a cui si riferisce) invece di centrarsi sull'intera colonna. */}
          <div className="flex items-start gap-3">
            {config.avatar.wake_word && !speaking && !listening && (
              <div className="relative" data-testid="kiosk-wake-hint">
                <div className="whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-semibold text-white" style={{ backgroundColor: "rgba(10,10,10,0.75)" }}>
                  {localizedText(
                    secondaryWakeEnabled && secondaryWakeWord ? config.avatar.wake_word_secondary_hint : config.avatar.wake_word_hint,
                    uiLang
                  )}
                </div>
                <div className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45" style={{ backgroundColor: "rgba(10,10,10,0.75)" }} />
              </div>
            )}
            <div className="flex flex-col gap-3">
              <button
                onClick={toggleMic} data-testid="kiosk-mic-btn"
                className={`flex h-16 w-16 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform ${listening ? "scale-110" : "hover:scale-105"}`}
                style={
                  listening
                    ? { boxShadow: `0 0 0 4px ${formButtonColor}` }
                    : speaking
                    ? { boxShadow: `0 0 0 4px ${micSpeakingRingColor}` }
                    : undefined
                }
              >
                {/* Richiesto dal cliente: icona "in ascolto" anche quando l'avatar sta parlando
                    rispondendo a una domanda (non durante il flusso deterministico, che è
                    nell'altro layer/fase "form" e non tocca questo pulsante) — non solo durante
                    l'ascolto vero e proprio. Testo "In ascolto…" resta legato al solo
                    `listening` reale, per non far credere che il microfono stia registrando
                    mentre l'avatar sta ancora parlando — ma l'anello cambia colore (blu del
                    progetto per l'ascolto, ambra fisso per il parlato) così restano comunque
                    distinguibili a colpo d'occhio pur condividendo la stessa icona. */}
                {listening || speaking ? <AudioLines className="h-7 w-7 animate-pulse" /> : <Mic className="h-7 w-7" />}
              </button>
              {/* Tasto refresh (richiesto esplicitamente dal cliente: "nella schermata iniziale
                  e mentre l'avatar parla") — sempre visibile nel layer voce, NON nascosto
                  mentre speaking/listening come il suggerimento della wake word sopra: riparte
                  da zero (stesso restart() del pulsante Annulla nel layer form) anche se
                  l'avatar sta ancora parlando o si è nel mezzo di un ascolto attivo. */}
              <button
                onClick={restart} data-testid="kiosk-refresh-btn"
                className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform hover:scale-105"
              >
                <RotateCcw className="h-7 w-7" />
              </button>
            </div>
          </div>
          {listening && <p className="text-xs" style={{ color: formButtonColor }} data-testid="kiosk-listening">In ascolto…</p>}

          {config.steps?.length > 0 && (
            // Tondo invece che a pillola (richiesto esplicitamente dal cliente: "tasto start
            // tondo e di colore diverso"), stessa misura/stile dei pulsanti circolari sopra
            // (mic/refresh) così si riconosce come appartenente alla stessa famiglia — "colore
            // diverso" qui si legge come "diverso dal bianco di mic/refresh", non un nuovo
            // blu: resta formButtonColor ("stesso blu"), che da solo ora basta a distinguerlo
            // visivamente dagli altri due, tondi ma bianchi. L'etichetta (prima dentro al
            // pulsante) è ora una didascalia sotto, dato che un cerchio non ha spazio per un
            // testo lungo come quello dei suggerimenti del flusso.
            <div className="flex flex-col items-center gap-1.5">
              <button
                onClick={enterForm} data-testid="kiosk-trigger-btn"
                className="flex h-16 w-16 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105"
                style={{ backgroundColor: formButtonColor }}
              >
                <ArrowRight className="h-7 w-7" />
              </button>
              <p className="max-w-[6rem] text-center font-display text-xs font-bold leading-tight text-white">
                {localizedText(config.avatar.start_button_label, uiLang) || "Start"}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Layer 2: form logic (azioni deterministiche) ----------
  const inputBorder = hexToRgba(formTextColor, 0.15);
  const panelBorder = hexToRgba(formTextColor, 0.14);
  return (
    <div className="relative flex h-screen flex-col bg-kiosk-bg text-white" data-testid="kiosk-root" data-kiosk-phase="form">
      <AvatarCanvas
        provider={config.avatar.provider} modelUrl={config.avatar.model_url} backgroundUrl={backgroundUrl}
        speaking={speaking} modelScale={modelScale} modelOffsetY={modelOffsetY} still={!speaking}
        idleClip={config.avatar.idle_animation_clip} speakingBodyClip={config.avatar.speaking_body_animation_clip}
        speakingLipsClip={config.avatar.speaking_lips_animation_clip}
        className="absolute inset-0 h-full w-full"
      />

      <div className="absolute right-4 top-6 z-20 flex flex-col items-center gap-3 sm:right-8 sm:top-10">
        <button
          onClick={toggleMic} data-testid="kiosk-mic-btn"
          className={`flex h-14 w-14 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform ${listening ? "scale-110" : "hover:scale-105"}`}
          style={listening ? { boxShadow: `0 0 0 4px ${formButtonColor}` } : undefined}
        >
          {listening ? <AudioLines className="h-6 w-6 animate-pulse" /> : <Mic className="h-6 w-6" />}
        </button>
        <button onClick={() => setKeyboardOpen(true)} data-testid="kiosk-keyboard-btn" className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform hover:scale-105">
          <Keyboard className="h-6 w-6" />
        </button>
        <button onClick={restart} data-testid="kiosk-restart-icon" className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform hover:scale-105">
          <RotateCcw className="h-6 w-6" />
        </button>
        {listening && <p className="text-center text-xs" style={{ color: formButtonColor }} data-testid="kiosk-listening">In ascolto…</p>}
      </div>

      {keyboardOpen && (
        <div className="absolute inset-0 z-30 flex flex-col bg-black/60 backdrop-blur-sm" data-testid="kiosk-keyboard-overlay">
          <div className="flex h-1/2 flex-col">
            <button onClick={() => setKeyboardOpen(false)} data-testid="kiosk-keyboard-close" className="ml-auto mr-4 mt-4 flex items-center gap-1 text-xs uppercase tracking-widest text-white/70 hover:text-white">
              <X className="h-4 w-4" /> Chiudi
            </button>
            <div className="mt-auto w-full px-6 pb-6">
              <div className="mx-auto w-full max-w-lg">
                <p className="mb-2 text-xs uppercase tracking-widest text-white/60">Scrivi il tuo messaggio</p>
                <div className="flex gap-2">
                  <input
                    autoFocus value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && chatInput.trim()) { sendChat(chatInput); setKeyboardOpen(false); } }}
                    placeholder="Scrivi qui…" data-testid="kiosk-chat-input"
                    className="flex-1 border px-4 py-3 text-base placeholder:opacity-40 focus:outline-none"
                    style={{ backgroundColor: formInputColor, color: formTextColor, borderColor: inputBorder }}
                  />
                  <button
                    onClick={() => { sendChat(chatInput); setKeyboardOpen(false); }}
                    disabled={sending || !chatInput.trim()} data-testid="kiosk-chat-send"
                    className="flex w-14 items-center justify-center text-white disabled:opacity-40" style={{ backgroundColor: formButtonColor }}
                  >
                    {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="h-1/2" />
        </div>
      )}

      <div
        className="absolute inset-x-0 bottom-0 z-10 h-[50vh] overflow-y-auto rounded-t-3xl px-6 py-6 sm:px-10 sm:py-7"
        style={{ backgroundColor: formPanelBg, borderTop: `1px solid ${panelBorder}`, color: formTextColor, ...formBlurStyle }}
        data-testid="kiosk-form-panel"
      >
        <div className="mx-auto w-full max-w-lg" data-testid={`kiosk-step-${currentStep.key}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs uppercase tracking-[0.3em] opacity-60">{config.project_name}</span>
            <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: formButtonColor }}>{currentStep.title}</p>
          </div>
          {stepText && <h2 className="mt-2 font-display text-xl font-bold leading-snug opacity-90">{stepText}</h2>}

          {reply && (
            <div className="mt-3 border-l-2 p-3 text-sm opacity-90" style={{ borderColor: formButtonColor, backgroundColor: hexToRgba(formTextColor, 0.06) }} data-testid="kiosk-reply">
              {reply}
            </div>
          )}

          {validationError && (
            <div className="mt-3 border-l-2 border-red-500 bg-red-500/10 p-3 text-sm text-red-300" data-testid="kiosk-validation-error">
              {validationError}
            </div>
          )}

          <div className="mt-4 space-y-2.5">
            {currentStep.elements.map((el) => {
              if (!isElementVisible(el, answers)) return null;
              if (el.kind === "text") {
                return (
                  <div key={el.id} className="space-y-1">
                    <label className="text-xs opacity-60">{el.label}</label>
                    <input
                      value={answers[el.field_key] || ""} onChange={(e) => setAnswer(el.field_key, e.target.value)}
                      placeholder={el.placeholder || ""} data-testid={`kiosk-field-${el.field_key}`}
                      className="w-full border px-4 py-2.5 text-base placeholder:opacity-40 focus:outline-none"
                      style={{ backgroundColor: formInputColor, color: formTextColor, borderColor: inputBorder }}
                    />
                  </div>
                );
              }
              if (el.kind === "checkbox") {
                return (
                  <label key={el.id} className="flex cursor-pointer items-start gap-3 text-sm opacity-80">
                    <input
                      type="checkbox" checked={!!answers[el.field_key]} onChange={(e) => setAnswer(el.field_key, e.target.checked)}
                      data-testid={`kiosk-checkbox-${el.field_key}`} className="mt-1 h-5 w-5" style={{ accentColor: formButtonColor }}
                    />
                    <span>{el.label}</span>
                  </label>
                );
              }
              if (el.kind === "file") {
                const done = !!answers[el.field_key];
                const busy = !!uploading[el.field_key];
                return (
                  <div key={el.id} className="space-y-1">
                    <label className="text-xs opacity-60">{el.label}</label>
                    <label
                      data-testid={`kiosk-file-${el.field_key}`}
                      className="flex cursor-pointer items-center gap-3 border px-4 py-3 text-base transition-colors"
                      style={{ backgroundColor: done ? hexToRgba(formButtonColor, 0.15) : formInputColor, borderColor: done ? formButtonColor : inputBorder }}
                    >
                      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : done ? <FileCheck2 className="h-5 w-5" style={{ color: formButtonColor }} /> : <Upload className="h-5 w-5 opacity-70" />}
                      <span className="opacity-90">
                        {busy ? "Caricamento…" : done ? "Documento caricato ✓ (tocca per sostituire)" : el.placeholder || "Tocca per caricare o scattare una foto"}
                      </span>
                      <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" disabled={busy} onChange={(e) => uploadFile(el, e.target.files?.[0])} />
                    </label>
                  </div>
                );
              }
              if (el.kind === "document") {
                const done = !!answers[el.field_key];
                const busy = !!uploading[el.field_key];
                return (
                  <div key={el.id} className="space-y-1">
                    <label className="text-xs opacity-60">{el.label}</label>
                    <button
                      type="button" disabled={busy} onClick={() => setCaptureEl(el)} data-testid={`kiosk-document-${el.field_key}`}
                      className="flex w-full items-center gap-3 border px-4 py-3 text-left text-base transition-colors disabled:opacity-60"
                      style={{ backgroundColor: done ? hexToRgba(formButtonColor, 0.15) : formInputColor, borderColor: done ? formButtonColor : inputBorder }}
                    >
                      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : done ? <FileCheck2 className="h-5 w-5" style={{ color: formButtonColor }} /> : <Camera className="h-5 w-5 opacity-70" />}
                      <span className="opacity-90">
                        {busy ? "Caricamento…" : done ? "Documento acquisito ✓ (tocca per ripetere)" : el.placeholder || "Tocca per scansionare con la fotocamera"}
                      </span>
                    </button>
                  </div>
                );
              }
              if (el.kind === "image") {
                // Sola visualizzazione: answers[field_key] è popolato dalla risposta di
                // un'azione precedente (vedi flattenScalarFields/onButton), tipicamente
                // il pulsante che ha portato a questo step — es. "qr_code_svg" di un ordine
                // di pagamento appena creato (markup SVG vettoriale, nitido su schermo
                // intero, a differenza di un data-url PNG rasterizzato). Se il valore è
                // markup grezzo (inizia con "<", non già un URL/data-url) lo avvolgiamo in
                // un data-url `image/svg+xml` invece di iniettarlo nel DOM (es. con
                // dangerouslySetInnerHTML): un <img> non esegue script/handler annidati nel
                // markup, a differenza di un SVG inlineato — più sicuro per markup che
                // arriva da un'API esterna (configurata dall'operatore, ma comunque fuori
                // dal nostro controllo). Se il valore manca del tutto (configurazione
                // errata, o lo step è raggiunto per altra via) mostra un segnaposto invece
                // di un <img> rotto.
                const raw = answers[el.field_key];
                const isSvg = (raw && raw.trim().startsWith("<")) || /\.svg(\?|$)/i.test(raw || "") || /image\/svg\+xml/i.test(raw || "");
                const src = raw && raw.trim().startsWith("<") ? `data:image/svg+xml;utf8,${encodeURIComponent(raw)}` : raw;
                return (
                  <div key={el.id} className="space-y-1">
                    {el.label && <label className="text-xs opacity-60">{el.label}</label>}
                    {src ? (
                      // Sfondo bianco di default per gli SVG (richiesto dal cliente: quelli che
                      // arriva da VivaWallet — es. il QR dello "Smart Checkout" — hanno sfondo
                      // trasparente e si vedono male sul pannello scuro del kiosk) — qui, non nel
                      // template HosPass, così vale subito senza dover ricaricare le azioni di
                      // default dal pannello admin. Un po' di padding fa anche da "quiet zone"
                      // intorno al QR, che altrimenti sarebbe a filo col bordo bianco.
                      <img
                        src={src} alt={el.label || "immagine"} data-testid={`kiosk-image-${el.field_key}`}
                        className={`mx-auto max-h-80 w-auto border ${isSvg ? "p-3" : ""}`}
                        style={{ borderColor: inputBorder, backgroundColor: isSvg ? "#ffffff" : undefined }}
                      />
                    ) : (
                      <p className="text-sm opacity-60">Immagine non disponibile.</p>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={el.id} onClick={() => onButton(el)} disabled={sending} data-testid={`kiosk-button-${el.id}`}
                  // +20% su richiesta esplicita del cliente: px-6 py-[1.05rem] text-[1.2rem]
                  // = px-5/py-3.5/text-base * 1.2.
                  className="flex w-full items-center justify-between border px-6 py-[1.05rem] text-left font-display text-[1.2rem] font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: hexToRgba(formButtonColor, 0.18), borderColor: hexToRgba(formButtonColor, 0.4) }}
                >
                  {el.label}
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                </button>
              );
            })}
            {/* Pulsante Annulla sempre visibile, con lo stesso ingombro/stile degli altri
                pulsanti dello step (richiesto esplicitamente dal team UX/UI: "deve essere un
                tasto come gli altri", non un link testuale nell'header) — permette di
                annullare l'operazione in corso in qualsiasi momento del flusso. Torna alla
                home voce (stesso comportamento di RETURN_TO_VOICE_KEY già usato ovunque nel
                flusso), non un "indietro" di un singolo step: uno stato già inviato al
                gestionale (es. un documento verificato, un check-in registrato) non è
                comunque annullabile tornando indietro — confermato con l'utente. Contorno
                neutro (colore del testo, non il colore tema dei pulsanti) per distinguerlo
                visivamente dalle azioni proprie dello step. */}
            <button
              type="button" onClick={restart} disabled={sending} data-testid="kiosk-cancel-flow"
              // +20% su richiesta esplicita del cliente, stesso calcolo degli altri pulsanti
              // dello step sopra.
              className="flex w-full items-center justify-center gap-2 border px-6 py-[1.05rem] text-center font-display text-[1.2rem] font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
              style={{ borderColor: hexToRgba(formTextColor, 0.3), color: formTextColor }}
            >
              <X className="h-4 w-4" /> Annulla
            </button>
          </div>
        </div>
      </div>
      {captureEl && (
        <DocumentCaptureOverlay
          instructions={captureEl.placeholder || "Inquadra il documento nel riquadro: lo scatto parte da solo."}
          accentColor={formButtonColor}
          onCapture={(blob) => { const el = captureEl; setCaptureEl(null); uploadFile(el, blob); }}
          onClose={() => setCaptureEl(null)}
        />
      )}
    </div>
  );
}
